import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { RequireAuth } from './Auth/RequireAuth';
import { AuthInitializer } from '@/Components/auth/AuthInitializer';
import NotFound from './NotFound';

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthInitializer>
        <Outlet/>
      </AuthInitializer>
    ),
    children: [
      {
        path: '/login',
        lazy: () => import("./Auth/Login").then(m => ({ Component: m.default })),
      },
      {
        path: '/forgot-password',
        lazy: () => import("./Auth/ForgotPassword").then(m => ({ Component: m.default })),
      },
      {
        path: '/reset-password',
        lazy: () => import("./Auth/ResetPassword").then(m => ({ Component: m.default })),
      },
      {
        path: '/change-password-required',
        lazy: () => import("./Auth/ChangePasswordRequired").then(m => ({ Component: m.default })),
      },
      {
        path: '/',
        element: <RequireAuth />,
        children: [
          {
            // Prévisualisation landing bénéficiaire (hors Layout admin,
            // rendu full-page pour fidélité)
            path: 'upsell/preview/:contractId',
            lazy: () => import("./UpSell/PreviewLanding").then(m => ({ Component: m.default })),
          },
          {
            path: '/',
            lazy: () => import("./Layout").then(m => ({ Component: m.Layout })),
            children: [
              {
                index: true,
                element: <Navigate to="/dashboard" replace />,
              },
              {
                path: 'dashboard',
                lazy: () => import("./Dashboard").then(m => ({ Component: m.default })),
              },
              {
                path: 'vendors',
                children: [
                  {
                    index: true,
                    lazy: () => import("./Vendors").then(m => ({ Component: m.default })),
                  },
                  {
                    path: 'create',
                    lazy: () => import("./Vendors/Create").then(m => ({ Component: m.default })),
                  },
                  {
                    path: 'presets',
                    lazy: () => import("./Vendors/Presets").then(m => ({ Component: m.default })),
                  },
                  {
                    path: ':uuid',
                    children: [
                      {
                        index: true,
                        lazy: () => import("./Vendors/VendorTabRedirect").then(m => ({ Component: m.default })),
                      },
                      {
                        path: ':tab',
                        lazy: () => import("./Vendors/VendorDetail").then(m => ({ Component: m.default })),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'repairers',
                lazy: () => import("./Repairers").then(m => ({ Component: m.default })),
              },
              {
                path: 'users',
                lazy: () => import("./Users").then(m => ({ Component: m.default })),
              },
              {
                path: 'users/create',
                lazy: () => import("./Users/InternalUserCreatePage").then(m => ({ Component: m.default })),
              },
              {
                path: 'users/:id',
                lazy: () => import("./Users/InternalUserDetailPage").then(m => ({ Component: m.default })),
              },
              {
                path: 'users/:id/edit',
                lazy: () => import("./Users/InternalUserEditPage").then(m => ({ Component: m.default })),
              },
              {
                path: 'external-users',
                lazy: () => import("./ExternalUsers").then(m => ({ Component: m.default })),
              },
              {
                path: 'external-users/create',
                lazy: () => import("./ExternalUsers/ExternalUserCreatePage").then(m => ({ Component: m.default })),
              },
              {
                path: 'external-users/:id',
                lazy: () => import("./ExternalUsers/ExternalUserDetailPage").then(m => ({ Component: m.default })),
              },
              {
                path: 'warranties',
                lazy: () => import("./Warranties").then(m => ({ Component: m.default })),
              },
              {
                path: 'warranties/create',
                lazy: () => import("./Warranties/WarrantyCreate").then(m => ({ Component: m.default })),
              },
              {
                path: 'warranties/presets',
                lazy: () => import("./Warranties/WarrantyPresets").then(m => ({ Component: m.default })),
              },
              {
                path: 'warranties/:id',
                lazy: () => import("./Warranties/WarrantyDetail").then(m => ({ Component: m.default })),
              },
              {
                path: 'delivery-proofs',
                lazy: () => import("./DeliveryProofs").then(m => ({ Component: m.default })),
              },
              {
                path: 'delivery-proofs/new',
                lazy: () => import("./DeliveryProofs/DeliveryProofNew").then(m => ({ Component: m.default })),
              },
              {
                path: 'contracts',
                lazy: () => import("./Contracts").then(m => ({ Component: m.default })),
              },
              {
                path: 'contracts/create',
                lazy: () => import("./Contracts/ContractCreate").then(m => ({ Component: m.default })),
              },
              {
                path: 'contracts/recalc-fact-retro',
                lazy: () => import("./Contracts/RecalcFactRetro").then(m => ({ Component: m.default })),
              },
              {
                path: 'contracts/:id',
                lazy: () => import("./Contracts/ContractDetail").then(m => ({ Component: m.default })),
              },
              {
                path: 'savs',
                lazy: () => import("./Savs").then(m => ({ Component: m.default })),
              },
              {
                path: 'savs/diagnostic',
                lazy: () => import("./Savs/Diagnostic").then(m => ({ Component: m.default })),
              },
              {
                path: 'presale-sheet',
                lazy: () => import("./PresaleSheet").then(m => ({ Component: m.default })),
              },
              {
                path: 'plv',
                lazy: () => import("./Plv").then(m => ({ Component: m.default })),
              },
              {
                path: 'catalog-pricing',
                lazy: () => import("./CatalogPricing").then(m => ({ Component: m.default })),
              },
              {
                path: 'assistance',
                children: [
                  {
                    index: true,
                    element: <Navigate to="/assistance/tarifs" replace />,
                  },
                  {
                    path: ':tab',
                    lazy: () => import("./Assistance").then(m => ({ Component: m.default })),
                  },
                ],
              },
              // Anciennes adresses, avant le regroupement sous un seul onglet de menu.
              {
                path: 'assistance-pricing',
                element: <Navigate to="/assistance/tarifs" replace />,
              },
              {
                path: 'mutuaide-export',
                element: <Navigate to="/assistance/flux" replace />,
              },
              // Nouveautés produit, côté LECTEUR : le fil et la page des nouveautés qui
              // ciblent les utilisateurs de l'admin (mêmes URLs que sur les autres
              // plateformes). La RÉDACTION est juste en dessous, sous `/news`.
              {
                path: 'nouveautes',
                lazy: () => import("./NewsReader").then(m => ({ Component: m.NewsFeedRoute })),
              },
              {
                path: 'nouveautes/:slug',
                lazy: () => import("./NewsReader").then(m => ({ Component: m.NewsDetailRoute })),
              },
              // Nouveautés produit : rédaction et publication. Les trois routes passent
              // par le garde `news.manage`.
              {
                path: 'news',
                lazy: () => import("./News/NewsAccessGuard").then(m => ({ Component: m.default })),
                children: [
                  {
                    index: true,
                    lazy: () => import("./News").then(m => ({ Component: m.default })),
                  },
                  {
                    path: 'create',
                    lazy: () => import("./News/NewsEditPage").then(m => ({ Component: m.default })),
                  },
                  {
                    path: ':id/edit',
                    lazy: () => import("./News/NewsEditPage").then(m => ({ Component: m.default })),
                  },
                ],
              },
              {
                path: 'invoice-fees',
                lazy: () => import("./InvoiceFees").then(m => ({ Component: m.default })),
              },
              {
                path: 'fee-credit-notes',
                lazy: () => import("./FeeCreditNotes").then(m => ({ Component: m.default })),
              },
              {
                path: 'upsell-fees',
                lazy: () => import("./UpsellFees").then(m => ({ Component: m.default })),
              },
              {
                path: 'invoices-admin',
                lazy: () => import("./Invoices").then(m => ({ Component: m.default })),
              },
              {
                path: 'sav/:id',
                lazy: () => import("./Savs/SavV2Details").then(m => ({ Component: m.default })),
              },
              {
                path: 'webhooks',
                lazy: () => import("./Webhooks").then(m => ({ Component: m.default })),
              },
              {
                path: 'webhooks/:uuid',
                lazy: () => import("./Webhooks/WebhookDetails").then(m => ({ Component: m.default })),
              },
              {
                path: 'profile',
                lazy: () => import("./Profile").then(m => ({ Component: m.default })),
              },
              {
                path: 'upsell/config',
                lazy: () => import("./UpSell/Config").then(m => ({ Component: m.default })),
              },
              {
                path: 'upsell/simulator',
                lazy: () => import("./UpSell/Simulator").then(m => ({ Component: m.default })),
              },
              {
                path: 'upsell/warranties',
                lazy: () => import("../../routes/upsell/AdminWarranties").then(m => ({ Component: m.default })),
              },
              {
                path: 'upsell/analytics',
                lazy: () => import("./UpSell/AnalyticsDashboard").then(m => ({ Component: m.default })),
              },
              {
                path: 'upsell/leads',
                lazy: () => import("./UpSell/LeadsList").then(m => ({ Component: m.default })),
              },
              {
                path: 'upsell/leads/:contractId',
                lazy: () => import("./UpSell/LeadDetail").then(m => ({ Component: m.default })),
              },
              {
                path: 'integrity-checks',
                lazy: () => import("./IntegrityChecks").then(m => ({ Component: m.default })),
              },
              {
                path: 'integrity-checks/:id',
                lazy: () => import("./IntegrityChecks/CheckDetailPage").then(m => ({ Component: m.default })),
              },
              {
                path: 'sav-coherence',
                lazy: () => import("./SavCoherence").then(m => ({ Component: m.default })),
              },
              {
                path: 'declaration-banner',
                lazy: () => import("./DeclarationBanner").then(m => ({ Component: m.default })),
              },
              {
                path: 'garant-coherence',
                lazy: () => import("./GarantCoherence").then(m => ({ Component: m.default })),
              },
            ],
          },
        ]
      }
    ],
  },

  {
    path: '*',
    element: <NotFound />,
  }
]);

export default router;
